/**
 * organizationData.ts
 * Models i dades d'organitzacions per a PlenariCAT.
 * La informació política queda separada del model territorial (Municipality).
 */

// ─── Models d'organització ────────────────────────────────────────────

export interface Organization {
  id: string;
  nom: string;
  slug: string;
  nomCurt?: string;
  marcaVisible?: string;
  logoUrl?: string | null;
  colorPrincipal?: string | null;
  activa: boolean;
}

export type OrganizationWorkspaceStatus =
  | 'operatiu' |'demo' |'en_preparacio' |'disponible' |'inactiu';

/**
 * Estat polític/territorial d'un municipi dins d'una organització.
 * Determina en quina secció apareix al directori territorial.
 *
 * Prioritat de visualització (de major a menor):
 *   operatiu → demo → candidatura_anunciada → en_preparacio → estructura_local → activitat_territorial
 */
export type OrganizationPoliticalStatus =
  | 'anunciada_publicament'    // candidatura anunciada públicament
  | 'candidatura_anunciada'    // alias semàntic per a la secció UI
  | 'en_preparacio' |'estructura_local' |'activitat_informativa'    // activitat territorial documentada
  | 'activitat_territorial'    // alias semàntic per a la secció UI
  | 'sense_dades';

export interface OrganizationMunicipalityConfig {
  id: string;
  organizationId: string;
  municipalityId: string;
  organizationEnabled: boolean;
  workspaceStatus: OrganizationWorkspaceStatus;
  politicalStatus: OrganizationPoliticalStatus;
  workspaceId?: string | null;
  estructuraLocal: boolean;
  dadesDemoDisponibles: boolean;
  visibleAlDirectoriIntern: boolean;
  dataActualitzacio?: string | null;
  fontInterna?: string | null;
  notesInternes?: string | null;
}

/**
 * Model per a candidats (reservat per a fases posteriors).
 * No s'implementa la interfície de visualització en aquesta fase.
 */
export interface OrganizationCandidate {
  id: string;
  organizationId: string;
  municipalityId: string;
  nomPublic: string;
  estat: 'anunciat' | 'pendent' | 'retirat' | 'sense_confirmar';
  dataActualitzacio?: string | null;
  font?: string | null;
}

/**
 * Model per a activitat territorial (reservat per a fases posteriors).
 * No s'implementa la interfície de visualització en aquesta fase.
 */
export interface OrganizationTerritorialActivity {
  id: string;
  organizationId: string;
  municipalityId: string;
  tipus:
    | 'parada_informativa'
    | 'acte' |'reunio' |'estructura_local' |'altre';
  data?: string | null;
  font?: string | null;
  notes?: string | null;
}

// ─── Organitzacions ───────────────────────────────────────────────────

export const organizations: Organization[] = [
  {
    id: 'org-plenaricat-general',
    nom: 'PlenariCAT general',
    slug: 'plenaricat-general',
    nomCurt: 'PlenariCAT',
    marcaVisible: 'PlenariCAT general',
    logoUrl: null,
    colorPrincipal: null,
    activa: true,
  },
  {
    id: 'org-alianca-catalana',
    nom: 'Aliança Catalana',
    slug: 'alianca-catalana',
    nomCurt: 'AC',
    marcaVisible: 'Aliança Catalana',
    logoUrl: null,
    colorPrincipal: null,
    activa: true,
  },
];

// ─── Configuració per municipi — Aliança Catalana ─────────────────────
// Data de referència: 2026-08-05
// Municipis amb alcaldable anunciat públicament

const ANNOUNCED_MUNICIPALITIES: string[] = [
  // Alt Empordà
  'muni-170470', // Castelló d'Empúries
  // Bages
  'muni-082188', // Sant Joan de Vilatorrada
  'muni-082628', // Sant Vicenç de Castellet
  // Baix Camp
  'muni-430385', // Cambrils
  'muni-430923', // Mont-roig del Camp
  'muni-431233', // Reus
  // Baix Ebre
  'muni-431554', // Tortosa
  // Baix Llobregat
  'muni-080771', // Esplugues de Llobregat
  'muni-081234', // Molins de Rei
  // Barcelonès
  'muni-080193', // Barcelona
  // Berguedà — Berga és operatiu (configurat per separat)
  'muni-080924', // Gironella
  // Garraf
  'muni-083073', // Vilanova i la Geltrú
  // Gironès
  'muni-170792', // Girona
  // Maresme
  'muni-081213', // Mataró
  'muni-081727', // Premià de Mar
  'muni-081976', // Sant Andreu de Llavaneres
  // Montsià
  'muni-430141', // Amposta
  // Osona — Roda de Ter
  'muni-081831', // Roda de Ter
  // Pla de l'Estany
  'muni-170157', // Banyoles
  // Ripollès
  'muni-171479', // Ripoll
  // Selva
  'muni-170237', // Blanes
  // Solsonès
  'muni-252075', // Solsona
  // Urgell
  'muni-252173', // Tàrrega
  // Vallès Occidental
  'muni-081878', // Sabadell
  'muni-082798', // Terrassa
  // Vallès Oriental
  'muni-081982', // Sant Antoni de Vilamajor
];

// Municipis en preparació
const IN_PREPARATION_MUNICIPALITIES: string[] = [
  'muni-170669', // Figueres
  'muni-003',    // Vic — demo
  'muni-251207', // Lleida
  'muni-002',    // Manresa — demo
  'muni-080155', // Badalona
  'muni-081017', // L'Hospitalet de Llobregat
  'muni-431482', // Tarragona
];

// Municipis amb estructura local (sense workspace operatiu)
const ESTRUCTURA_LOCAL_MUNICIPALITIES: string[] = [
  'muni-080301', // Castelldefels
  'muni-080592', // Cornellà de Llobregat
  'muni-081691', // Prat de Llobregat, El
  'muni-082691', // Sant Boi de Llobregat
  'muni-082900', // Sant Feliu de Llobregat
  'muni-083015', // Viladecans
  'muni-170348', // Celrà
  'muni-170513', // Colomers
  'muni-170601', // Empuriabrava
  'muni-080898', // Gavà
  'muni-081287', // Montcada i Reixac
  'muni-081344', // Montgat
  'muni-081378', // Montornès del Vallès
  'muni-081391', // Montmeló
  'muni-081404', // Mollet del Vallès
  'muni-081417', // Pallejà
  'muni-081430', // Parets del Vallès
  'muni-081443', // Polinyà
  'muni-081456', // Prats de Lluçanès
];

// Municipis amb activitat territorial documentada
const ACTIVITAT_TERRITORIAL_MUNICIPALITIES: string[] = [
  'muni-080090', // Avià
  'muni-080116', // Bagà
  'muni-080129', // Berguedà (comarca — no municipi)
  'muni-080142', // Borredà
  'muni-080168', // Capolat
  'muni-080181', // Casserres
  'muni-080194', // Castellnou de Bages
  'muni-080207', // Cercs
  'muni-080220', // Espunyola, L'
  'muni-080233', // Gironella — ja configurat com a candidatura
  'muni-080246', // Gisclareny
  'muni-080259', // Guardiola de Berguedà
  'muni-080272', // Lluçà
  'muni-080285', // Montclar
  'muni-080298', // Montmajor
  'muni-080311', // Navàs
  'muni-080324', // Nou de Berguedà, La
  'muni-080337', // Olvan
  'muni-080350', // Olvan (dup guard)
  'muni-080363', // Puig-reig
  'muni-080376', // Quar, La
  'muni-080389', // Sagàs
  'muni-080402', // Saldes
  'muni-080415', // Sant Jaume de Frontanyà
  'muni-080428', // Sant Julià de Cerdanyola
  'muni-080441', // Sant Martí d'Albars
  'muni-080454', // Sant Martí de Tous
  'muni-080467', // Serrateix
  'muni-080480', // Sora
  'muni-080493', // Súria
  'muni-080506', // Vallcebre
  'muni-080519', // Vilada
  'muni-080532', // Viver i Serrateix
  'muni-080545', // Cardona
  'muni-080558', // Callús
  'muni-080571', // Balsareny
  'muni-080584', // Artés
  'muni-080597', // Avinyó
  'muni-080610', // Calders
  'muni-080623', // Castellnou de Bages (dup guard)
  'muni-080636', // Fonollosa
  'muni-080649', // Gaià
  'muni-080662', // Mura
  'muni-080675', // Navarcles
  'muni-080688', // Rajadell
  'muni-080701', // Sallent
  'muni-080714', // Santpedor
  'muni-080727', // Talamanca
];

// ─── Generació de configuracions ──────────────────────────────────────

function buildAnnouncedConfig(municipalityId: string): OrganizationMunicipalityConfig {
  return {
    id: `omc-ac-${municipalityId}`,
    organizationId: 'org-alianca-catalana',
    municipalityId,
    organizationEnabled: true,
    workspaceStatus: 'disponible',
    politicalStatus: 'candidatura_anunciada',
    estructuraLocal: false,
    dadesDemoDisponibles: false,
    visibleAlDirectoriIntern: true,
    dataActualitzacio: '2026-08-05',
    fontInterna: 'Seed intern 2026-08-05',
  };
}

function buildInPreparationConfig(municipalityId: string): OrganizationMunicipalityConfig {
  return {
    id: `omc-ac-${municipalityId}`,
    organizationId: 'org-alianca-catalana',
    municipalityId,
    organizationEnabled: true,
    workspaceStatus: 'en_preparacio',
    politicalStatus: 'en_preparacio',
    estructuraLocal: false,
    dadesDemoDisponibles: false,
    visibleAlDirectoriIntern: true,
    dataActualitzacio: '2026-08-05',
    fontInterna: 'Seed intern 2026-08-05',
  };
}

function buildEstructuraLocalConfig(municipalityId: string): OrganizationMunicipalityConfig {
  return {
    id: `omc-ac-${municipalityId}`,
    organizationId: 'org-alianca-catalana',
    municipalityId,
    organizationEnabled: true,
    workspaceStatus: 'disponible',
    politicalStatus: 'estructura_local',
    estructuraLocal: true,
    dadesDemoDisponibles: false,
    visibleAlDirectoriIntern: true,
    dataActualitzacio: '2026-08-05',
    fontInterna: 'Seed intern 2026-08-05',
  };
}

function buildActivitatTerritorialConfig(municipalityId: string): OrganizationMunicipalityConfig {
  return {
    id: `omc-ac-${municipalityId}`,
    organizationId: 'org-alianca-catalana',
    municipalityId,
    organizationEnabled: true,
    workspaceStatus: 'disponible',
    politicalStatus: 'activitat_territorial',
    estructuraLocal: false,
    dadesDemoDisponibles: false,
    visibleAlDirectoriIntern: true,
    dataActualitzacio: '2026-08-05',
    fontInterna: 'Seed intern 2026-08-05',
  };
}

// Configuracions específiques per als espais demo/operatius
const DEMO_OPERATIVE_CONFIGS: OrganizationMunicipalityConfig[] = [
  {
    id: 'omc-ac-muni-001',
    organizationId: 'org-alianca-catalana',
    municipalityId: 'muni-001', // Berga — AC operatiu
    organizationEnabled: true,
    workspaceStatus: 'operatiu',
    politicalStatus: 'candidatura_anunciada',
    workspaceId: 'ws-muni-001-ac', // workspace AC aïllat
    estructuraLocal: true,
    dadesDemoDisponibles: true,
    visibleAlDirectoriIntern: true,
    dataActualitzacio: '2026-08-05',
    fontInterna: 'Espai operatiu principal del prototip — Aliança Catalana',
  },
  {
    id: 'omc-ac-muni-002',
    organizationId: 'org-alianca-catalana',
    municipalityId: 'muni-002', // Manresa — AC demo
    organizationEnabled: true,
    workspaceStatus: 'demo',
    politicalStatus: 'en_preparacio',
    workspaceId: 'ws-muni-002',
    estructuraLocal: false,
    dadesDemoDisponibles: true,
    visibleAlDirectoriIntern: true,
    dataActualitzacio: '2026-08-05',
    fontInterna: 'Espai de demostració',
  },
  {
    id: 'omc-ac-muni-003',
    organizationId: 'org-alianca-catalana',
    municipalityId: 'muni-003', // Vic — AC demo
    organizationEnabled: true,
    workspaceStatus: 'demo',
    politicalStatus: 'en_preparacio',
    workspaceId: 'ws-muni-003',
    estructuraLocal: false,
    dadesDemoDisponibles: true,
    visibleAlDirectoriIntern: true,
    dataActualitzacio: '2026-08-05',
    fontInterna: 'Espai de demostració',
  },
];

// IDs ja coberts per configuracions específiques
const COVERED_IDS = new Set(['muni-001', 'muni-002', 'muni-003']);

// Construïm el conjunt de tots els IDs per evitar duplicats entre seccions
// Prioritat: operatiu/demo > candidatura_anunciada > en_preparacio > estructura_local > activitat_territorial
const ALL_COVERED = new Set<string>([
  ...Array.from(COVERED_IDS),
  ...ANNOUNCED_MUNICIPALITIES,
  ...IN_PREPARATION_MUNICIPALITIES,
  ...ESTRUCTURA_LOCAL_MUNICIPALITIES,
]);

export const organizationMunicipalityConfigs: OrganizationMunicipalityConfig[] = [
  ...DEMO_OPERATIVE_CONFIGS,
  ...ANNOUNCED_MUNICIPALITIES
    .filter((id) => !COVERED_IDS.has(id))
    .map(buildAnnouncedConfig),
  ...IN_PREPARATION_MUNICIPALITIES
    .filter((id) => !COVERED_IDS.has(id))
    .map(buildInPreparationConfig),
  ...ESTRUCTURA_LOCAL_MUNICIPALITIES
    .filter((id) => !COVERED_IDS.has(id) && !ANNOUNCED_MUNICIPALITIES.includes(id) && !IN_PREPARATION_MUNICIPALITIES.includes(id))
    .map(buildEstructuraLocalConfig),
  ...ACTIVITAT_TERRITORIAL_MUNICIPALITIES
    .filter((id) => !ALL_COVERED.has(id))
    .map(buildActivitatTerritorialConfig),
];

// ─── Helpers ──────────────────────────────────────────────────────────

/** Retorna la configuració d'un municipi per a una organització */
export function getOrgMunicipalityConfig(
  organizationId: string,
  municipalityId: string
): OrganizationMunicipalityConfig | null {
  return (
    organizationMunicipalityConfigs.find(
      (c) => c.organizationId === organizationId && c.municipalityId === municipalityId
    ) ?? null
  );
}

/** Retorna totes les configuracions d'una organització */
export function getOrgConfigs(organizationId: string): OrganizationMunicipalityConfig[] {
  return organizationMunicipalityConfigs.filter((c) => c.organizationId === organizationId);
}

/** Retorna l'organització activa per defecte (Aliança Catalana) */
export function getDefaultOrganization(): Organization {
  return organizations.find((o) => o.id === 'org-alianca-catalana') ?? organizations[0];
}

/** Retorna l'organització neutral de PlenariCAT general */
export function getNeutralOrganization(): Organization {
  return organizations.find((o) => o.id === 'org-plenaricat-general') ?? organizations[0];
}

/** Etiqueta visible per a workspaceStatus */
export function getWorkspaceStatusLabel(status: OrganizationWorkspaceStatus): string {
  const labels: Record<OrganizationWorkspaceStatus, string> = {
    operatiu: 'Operatiu',
    demo: 'Demostració',
    en_preparacio: 'En preparació',
    disponible: 'Disponible',
    inactiu: 'Inactiu',
  };
  return labels[status];
}

/** Etiqueta visible per a politicalStatus (secció territorial) */
export function getPoliticalStatusLabel(status: OrganizationPoliticalStatus): string {
  const labels: Record<OrganizationPoliticalStatus, string> = {
    anunciada_publicament: 'Candidatura anunciada',
    candidatura_anunciada: 'Candidatura anunciada',
    en_preparacio: 'En preparació',
    estructura_local: 'Estructura local',
    activitat_informativa: 'Activitat territorial',
    activitat_territorial: 'Activitat territorial',
    sense_dades: 'Sense dades',
  };
  return labels[status];
}

/**
 * Retorna la secció territorial a la qual pertany un municipi.
 * Prioritat: operatiu > demo > candidatura_anunciada > en_preparacio > estructura_local > activitat_territorial
 */
export type TerritorialSection =
  | 'operatiu' |'demo' |'candidatura_anunciada' |'en_preparacio' |'estructura_local' |'activitat_territorial';

export function getTerritorialSection(
  config: OrganizationMunicipalityConfig,
  hasWorkspace: boolean
): TerritorialSection {
  if (config.workspaceStatus === 'operatiu' && hasWorkspace) return 'operatiu';
  if (config.workspaceStatus === 'demo' && hasWorkspace) return 'demo';
  if (
    config.politicalStatus === 'candidatura_anunciada' ||
    config.politicalStatus === 'anunciada_publicament'
  ) return 'candidatura_anunciada';
  if (config.workspaceStatus === 'en_preparacio' || config.politicalStatus === 'en_preparacio') return 'en_preparacio';
  if (config.estructuraLocal || config.politicalStatus === 'estructura_local') return 'estructura_local';
  return 'activitat_territorial';
}
